import { api, apiOrigin } from './api';
import type {
  AppNotification,
  NotificationSettings,
  ParameterType,
  SystemParameter,
  WorkspaceTheme,
  ApiKey,
  AutomationExecution,
  AutomationExecutionStatus,
  AutomationExecutionSummary,
  Automation,
  AutomationBlock,
  AutomationImportReport,
  AutomationNote,
  AutomationVersionSummary,
  ChatConversation,
  ChatMessage,
  ChatThread,
  LeadConversation,
  MultiserviceDashboard,
  ChannelConnection,
  ChannelProviderName,
  Department,
  DepartmentMember,
  MemberMultiservice,
  WorkingHours,
  ActivityDashboard,
  BusinessDashboard,
  PipelineDashboard,
  ActionPreview,
  AdditionalFieldValue,
  BulkReport,
  JourneyEntry,
  MemberPipelineRow,
  PipelineComplete,
  PipelineMemberFlags,
  PipelineMemberRow,
  StageAutoActivity,
  StageRule,
  Activity,
  ActivityType,
  Attachment,
  Business,
  BusinessStatus,
  Company,
  CreatedInvitation,
  CustomField,
  CustomFieldEntity,
  CustomFieldGroup,
  CustomFieldStats,
  Attendant,
  HierarchyNode,
  HistoryCategory,
  HistoryEntry,
  ImportPreview,
  Invitation,
  Job,
  LeadAddressRow,
  LeadKind,
  LeadRelations,
  RelationKind,
  SavedView,
  InvitationPreview,
  Member,
  PermissionCode,
  PhoneSettings,
  Profile,
  SessionInfo,
  AuthSession,
  TrashItem,
  Lead,
  List,
  LossReason,
  Paginated,
  Pipeline,
  Product,
  Stage,
  StageSummary,
  Tag,
} from '../types';

/** Camada fina sobre a API: um lugar só onde as rotas do contrato aparecem. */

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> =>
  promise.then((response) => response.data);

// --------------------------------------------------------------- pipelines

export const listPipelines = () =>
  unwrap<Paginated<Pipeline>>(api.get('/pipelines', { params: { skip: 0, take: 200 } }));

export const listStages = (pipelineId: string) =>
  unwrap<Stage[]>(api.get(`/pipelines/${pipelineId}/stages`));

/** Pipelines, etapas e as abas de configuração (DC1c). */
export const pipelinesApi = {
  get: (id: string) => unwrap<PipelineComplete>(api.get(`/pipelines/${id}`)),
  groups: () => unwrap<string[]>(api.get('/pipelines/groups')),
  create: (payload: {
    name: string;
    description?: string;
    group?: string;
    stages?: Array<{ name: string; color?: string }>;
  }) => unwrap<PipelineComplete>(api.post('/pipelines', payload)),
  update: (
    id: string,
    payload: { name?: string; description?: string | null; group?: string | null },
  ) => unwrap<PipelineComplete>(api.put(`/pipelines/${id}`, payload)),
  remove: (id: string) => api.delete(`/pipelines/${id}`).then(() => undefined),
  duplicate: (id: string) => unwrap<PipelineComplete>(api.post(`/pipelines/${id}/duplicate`)),
  reorder: (items: Array<{ id: string; group?: string | null; index: number }>) =>
    unwrap<Pipeline[]>(api.patch('/pipelines/reorder', { items })),
  favorite: (id: string, on: boolean) =>
    (on ? api.put(`/pipelines/${id}/favorite`) : api.delete(`/pipelines/${id}/favorite`)).then(
      () => undefined,
    ),

  createStage: (pipelineId: string, payload: { name: string; color?: string }) =>
    unwrap<Stage>(api.post(`/pipelines/${pipelineId}/stages`, payload)),
  updateStage: (id: string, payload: { name?: string; color?: string }) =>
    unwrap<Stage>(api.patch(`/stages/${id}`, payload)),
  reorderStages: (pipelineId: string, ids: string[]) =>
    unwrap<Stage[]>(api.patch(`/pipelines/${pipelineId}/stages/reorder`, { ids })),
  removeStage: (id: string, moveTo?: string) =>
    unwrap<{ moved: number }>(api.delete(`/stages/${id}`, { params: { moveTo } })),

  members: (id: string) => unwrap<PipelineMemberRow[]>(api.get(`/pipelines/${id}/members`)),
  setMembers: (
    id: string,
    members: Array<Partial<PipelineMemberFlags> & { membershipId: string }>,
  ) => unwrap<PipelineMemberRow[]>(api.put(`/pipelines/${id}/members`, { members })),
  rules: (id: string) => unwrap<StageRule[]>(api.get(`/pipelines/${id}/rules`)),
  setRules: (id: string, rules: StageRule[]) =>
    unwrap<StageRule[]>(api.put(`/pipelines/${id}/rules`, { rules })),
  autoActivities: (id: string) =>
    unwrap<StageAutoActivity[]>(api.get(`/pipelines/${id}/auto-activities`)),
  createAutoActivity: (stageId: string, payload: Partial<StageAutoActivity>) =>
    unwrap<StageAutoActivity>(api.post(`/stages/${stageId}/auto-activities`, payload)),
  updateAutoActivity: (stageId: string, id: string, payload: Partial<StageAutoActivity>) =>
    unwrap<StageAutoActivity>(api.put(`/stages/${stageId}/auto-activities/${id}`, payload)),
  removeAutoActivity: (stageId: string, id: string) =>
    api.delete(`/stages/${stageId}/auto-activities/${id}`).then(() => undefined),

  /** Bloco "CRM e pipelines" da tela de Membros. */
  ofMember: (membershipId: string) =>
    unwrap<MemberPipelineRow[]>(api.get(`/members/${membershipId}/pipelines`)),
  setOfMember: (
    membershipId: string,
    pipelines: Array<Partial<PipelineMemberFlags> & { pipelineId: string }>,
  ) =>
    unwrap<MemberPipelineRow[]>(api.put(`/members/${membershipId}/pipelines`, { pipelines })),
};

// ------------------------------------------------------------------- leads

export interface LeadFilters {
  skip?: number;
  take?: number;
  search?: string;
  searchType?: 'name' | 'phone' | 'email';
  kind?: LeadKind;
  tags?: string;
  lists?: string;
  additionalField?: string;
  attendant?: string;
  source?: string;
  complete?: boolean;
}

export const listLeads = (params: LeadFilters = {}) =>
  unwrap<Paginated<Lead>>(api.get('/leads', { params }));

/**
 * Lista pela querystring dos filtros — a mesma da URL da tela, das visões e das
 * ações em massa (campo adicional pode repetir, por isso não passa por `params`).
 */
export const listLeadsByQuery = (filter: string, skip: number, take: number) => {
  const params = new URLSearchParams(filter);
  params.set('skip', String(skip));
  params.set('take', String(take));
  return unwrap<Paginated<Lead>>(api.get(`/leads?${params.toString()}`));
};

export const previewLeads = (filter: string) =>
  unwrap<{ count: number }>(api.get(`/leads/actions/preview?${filter}`));

export const listJobTitles = () => unwrap<string[]>(api.get('/leads/job-titles'));

export const getLead = (id: string) => unwrap<Lead>(api.get(`/leads/${id}`));

export interface LeadPayload {
  kind?: LeadKind;
  name: string;
  displayName?: string | null;
  tradeName?: string | null;
  jobTitle?: string | null;
  segment?: string | null;
  foundationDate?: string | null;
  notes?: string | null;
  companyId?: string;
  primaryContactId?: string;
  parentCompanyId?: string;
  phone?: string;
  email?: string;
  company?: string;
  source?: string;
  taxId?: string;
  site?: string;
  instagram?: string;
  birthDate?: string;
  address?: Lead['address'];
  tags?: string[];
  lists?: string[];
  attendant?: string | null;
  additionalFields?: Array<{ id: string; value: string | null }>;
}

export const createLead = (payload: LeadPayload) => unwrap<Lead>(api.post('/leads', payload));

export const updateLead = (
  id: string,
  payload: Partial<
    Omit<LeadPayload, 'kind' | 'companyId' | 'primaryContactId' | 'parentCompanyId'>
  >,
) => unwrap<Lead>(api.patch(`/leads/${id}`, payload));

export const deleteLead = (id: string) => api.delete(`/leads/${id}`).then(() => undefined);

export const listLeadBusinesses = (
  id: string,
  params: {
    scope?: 'lead' | 'company' | 'both';
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  } = {},
) => unwrap<Business[]>(api.get(`/leads/${id}/businesses`, { params }));

export const listLeadHistory = (id: string, businessId?: string, category?: HistoryCategory) =>
  unwrap<HistoryEntry[]>(api.get(`/leads/${id}/history`, { params: { businessId, category } }));

export const listLeadNotes = (id: string) =>
  unwrap<HistoryEntry[]>(api.get(`/leads/${id}/notes`));

export const addLeadNote = (id: string, note: string, businessId?: string) =>
  unwrap<HistoryEntry>(api.post(`/leads/${id}/notes`, { note, businessId }));

export const removeLeadNote = (leadId: string, noteId: string) =>
  api.delete(`/leads/${leadId}/notes/${noteId}`).then(() => undefined);

// -------------------------------------------------------------- businesses

export interface BusinessFilters {
  skip?: number;
  take?: number;
  search?: string;
  pipelineId?: string;
  stageId?: string;
  leadId?: string;
  status?: BusinessStatus | 'all';
  tags?: string;
  minTotal?: number;
  maxTotal?: number;
  sort?: 'recent' | 'oldest' | 'moved_recent' | 'moved_oldest';
}

/** `filter` é a querystring dos filtros da tela (mesmos nomes da API). */
export const listBusinesses = (params: BusinessFilters = {}, filter = '') =>
  unwrap<Paginated<Business>>(api.get(`/businesses${filter ? `?${filter}` : ''}`, { params }));

export const getBusiness = (id: string) => unwrap<Business>(api.get(`/businesses/${id}`));

export const businessSummary = (pipelineId: string, filter = '') =>
  unwrap<StageSummary[]>(
    api.get(`/businesses/summary${filter ? `?${filter}` : ''}`, { params: { pipelineId } }),
  );

export const businessJourney = (id: string) =>
  unwrap<JourneyEntry[]>(api.get(`/businesses/${id}/journey`));

export const businessFields = (id: string) =>
  unwrap<AdditionalFieldValue[]>(api.get(`/businesses/${id}/additional-fields`));

export const setBusinessField = (id: string, fieldId: string, value: string | null) =>
  unwrap<AdditionalFieldValue[]>(
    api.put(`/businesses/${id}/additional-fields/${fieldId}`, { value }),
  );

/** Alvo de ação em lote: a seleção ou o filtro da pipeline (querystring da tela). */
export type BusinessTarget =
  { ids: string[] } | { pipelineId: string; stageId?: string; filter?: string };

export type BusinessAction = 'move' | 'win' | 'lose' | 'restore' | 'delete';

export const businessActions = {
  preview: (action: BusinessAction, target: BusinessTarget, destinationStageId?: string) =>
    unwrap<ActionPreview>(
      api.post('/businesses/actions/preview', { action, destinationStageId, ...target }),
    ),
  move: (target: BusinessTarget, destinationStageId: string) =>
    unwrap<BulkReport>(
      api.post('/businesses/actions/move', { ...target, destinationStageId, report: true }),
    ),
  win: (target: BusinessTarget) =>
    unwrap<BulkReport>(api.post('/businesses/actions/win', { ...target, report: true })),
  lose: (target: BusinessTarget, lossReasonId: string, justification?: string) =>
    unwrap<BulkReport>(
      api.post('/businesses/actions/lose', {
        ...target,
        lossReasonId,
        justification,
        report: true,
      }),
    ),
  restore: (target: BusinessTarget) =>
    unwrap<BulkReport>(api.post('/businesses/actions/restore', { ...target, report: true })),
  remove: (target: BusinessTarget) =>
    unwrap<BulkReport>(api.post('/businesses/actions/delete', { ...target, report: true })),
};

export const createBusiness = (payload: {
  leadId: string;
  stageId: string;
  attendantId?: string;
  total?: number;
}) => unwrap<Business>(api.post('/businesses', payload));

export const updateBusiness = (
  id: string,
  payload: Partial<{
    total: number;
    discount: number;
    addition: number;
    shipping: number;
    coupon: string;
    shippingType: string;
    attendantId: string;
  }>,
) => unwrap<Business>(api.patch(`/businesses/${id}`, payload));

export const deleteBusiness = (id: string) =>
  api.delete(`/businesses/${id}`).then(() => undefined);

export const moveBusinesses = (ids: string[], destinationStageId: string) =>
  unwrap<Business[]>(api.post('/businesses/actions/move', { ids, destinationStageId }));

export const winBusinesses = (ids: string[]) =>
  unwrap<Business[]>(api.post('/businesses/actions/win', { ids }));

export const loseBusinesses = (ids: string[], lossReasonId: string, justification?: string) =>
  unwrap<Business[]>(
    api.post('/businesses/actions/lose', { ids, lossReasonId, justification }),
  );

export const restoreBusinesses = (ids: string[]) =>
  unwrap<Business[]>(api.post('/businesses/actions/restore', { ids }));

export const addBusinessProduct = (
  businessId: string,
  payload: { productId: string; quantity?: number; price?: number },
) => unwrap<Business>(api.post(`/businesses/${businessId}/products`, payload));

export const updateBusinessProduct = (
  businessId: string,
  itemId: string,
  payload: { quantity?: number; price?: number },
) => unwrap<Business>(api.put(`/businesses/${businessId}/products/${itemId}`, payload));

export const removeBusinessProduct = (businessId: string, itemId: string) =>
  unwrap<Business>(api.delete(`/businesses/${businessId}/products/${itemId}`));

// ---------------------------------------------------------------- catálogos

const catalog = <T>(resource: string) => ({
  list: (params: { skip?: number; take?: number; search?: string } = {}) =>
    unwrap<Paginated<T>>(api.get(`/${resource}`, { params: { take: 100, ...params } })),
  create: (payload: Partial<T>) => unwrap<T>(api.post(`/${resource}`, payload)),
  update: (id: string, payload: Partial<T>) =>
    unwrap<T>(api.put(`/${resource}/${id}`, payload)),
  /** Vai para a Lixeira. */
  remove: (id: string) => api.delete(`/${resource}/${id}`).then(() => undefined),
  /** Exclusão em massa da seleção — tudo ou nada. */
  removeMany: (ids: string[]) =>
    unwrap<{ deleted: number }>(api.post(`/${resource}/actions/delete`, { ids })),
});

export const tags = catalog<Tag>('tags');
export const lists = catalog<List>('lists');
export const products = catalog<Product>('products');
export const lossReasons = catalog<LossReason>('business-loss-reasons');

/** Campos adicionais: lista por aba, na ordem da tela. */
export const customFields = {
  ...catalog<CustomField>('additional-fields'),
  list: (params: { entity?: CustomFieldEntity; search?: string } = {}) =>
    unwrap<Paginated<CustomField>>(
      api.get('/additional-fields', { params: { take: 200, ...params } }),
    ),
  reorder: (items: Array<{ id: string; groupId: string | null; position: number }>) =>
    unwrap<CustomField[]>(api.patch('/additional-fields/reorder', { items })),
  stats: (id: string) => unwrap<CustomFieldStats>(api.get(`/additional-fields/${id}/stats`)),
};

export const customFieldGroups = {
  ...catalog<CustomFieldGroup>('additional-field-groups'),
  list: (entity?: CustomFieldEntity) =>
    unwrap<Paginated<CustomFieldGroup>>(
      api.get('/additional-field-groups', { params: { entity } }),
    ),
};

// ------------------------------------------------------------- atividades

export interface ActivityFilters {
  skip?: number;
  take?: number;
  search?: string;
  leadId?: string;
  businessId?: string;
  attendant?: string;
  activityTypeId?: string;
  isCompleted?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
}

export const listActivities = (params: ActivityFilters = {}) =>
  unwrap<Paginated<Activity>>(api.get('/activities', { params }));

export const listLeadActivities = (leadId: string) =>
  unwrap<Activity[]>(api.get(`/leads/${leadId}/activities`));

export interface ActivityPayload {
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  activityTypeId?: string;
  leadId?: string;
  businessId?: string;
  required?: boolean;
}

export const createActivity = (payload: ActivityPayload) =>
  unwrap<Activity>(api.post('/activities', payload));

export const updateActivity = (
  id: string,
  payload: Partial<ActivityPayload> & { isCompleted?: boolean },
) => unwrap<Activity>(api.patch(`/activities/${id}`, payload));

export const deleteActivity = (id: string) =>
  api.delete(`/activities/${id}`).then(() => undefined);

export const activityTypes = catalog<ActivityType>('activity-types');

// ----------------------------------------------------------------- anexos

export const listLeadAttachments = (leadId: string) =>
  unwrap<Paginated<Attachment>>(
    api.get(`/leads/${leadId}/attachments`, { params: { take: 100 } }),
  );

export const listBusinessAttachments = (businessId: string) =>
  unwrap<Paginated<Attachment>>(
    api.get(`/businesses/${businessId}/attachments`, { params: { take: 100 } }),
  );

export const uploadLeadAttachment = (
  leadId: string,
  file: File,
  extra: { businessId?: string; description?: string } = {},
) => {
  const form = new FormData();
  form.append('file', file);
  if (extra.businessId) form.append('businessId', extra.businessId);
  if (extra.description) form.append('description', extra.description);

  return unwrap<Attachment>(api.post(`/leads/${leadId}/attachments`, form));
};

export const deleteLeadAttachment = (leadId: string, attachmentId: string) =>
  api.delete(`/leads/${leadId}/attachments/${attachmentId}`).then(() => undefined);

/** Anexo local é servido pela API e exige o Bearer — não dá para usar <a href> puro. */
export const isLocalAttachment = (attachment: Attachment): boolean =>
  attachment.url.startsWith('/api/v1/attachments/');

/**
 * Baixa o anexo autenticado: busca como blob e dispara o save pelo navegador.
 * Para anexo hospedado fora, só abre a URL.
 */
export const downloadAttachment = async (attachment: Attachment): Promise<void> => {
  if (!isLocalAttachment(attachment)) {
    window.open(attachment.url, '_blank', 'noopener');
    return;
  }

  const response = await api.get(`/attachments/${attachment.id}/download`, {
    responseType: 'blob',
  });

  const objectUrl = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = attachment.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

// ------------------------------------------------------------ Configurações

export const company = {
  get: () => unwrap<Company>(api.get('/workspaces/current')),
  update: (payload: Partial<Company>) =>
    unwrap<Company>(api.patch('/workspaces/current', payload)),
  updateAddress: (payload: Company['address'] & { taxId?: string | null }) =>
    unwrap<Company>(api.patch('/workspaces/current/address', payload)),
  updatePhoneSettings: (payload: Partial<PhoneSettings>) =>
    unwrap<Company>(api.patch('/workspaces/current/phone-settings', payload)),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return unwrap<Company>(api.post('/workspaces/current/logo', form));
  },
  removeLogo: () => unwrap<Company>(api.delete('/workspaces/current/logo')),
  /** Aparência (Fase 5 / P2): substitui o tema inteiro; `{}` volta ao padrão. */
  updateTheme: (theme: WorkspaceTheme) =>
    unwrap<Company>(api.put('/workspaces/current/theme', theme)),
};

/** O logo é servido pela API sem token; só falta o host. */
export const apiAssetUrl = (path: string | null | undefined): string | null => {
  if (!path) return null;
  const base = apiOrigin();
  return `${base}${path}`;
};

export const profile = {
  get: () => unwrap<Profile>(api.get('/users/me')),
  update: (payload: Partial<Pick<Profile, 'name' | 'phone' | 'timezone'>>) =>
    unwrap<Profile>(api.patch('/users/me', payload)),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/users/me/password', { currentPassword, newPassword }).then(() => undefined),
};

export const members = {
  list: (search?: string) =>
    unwrap<Paginated<Member>>(api.get('/members', { params: { take: 200, search } })),
  updatePermissions: (id: string, isAdmin: boolean, permissions: PermissionCode[]) =>
    unwrap<Member>(api.put(`/members/${id}/permissions`, { isAdmin, permissions })),
  forceLogout: (id: string) => unwrap<Member>(api.post(`/members/${id}/force-logout`)),
  remove: (id: string) => api.delete(`/members/${id}`).then(() => undefined),
};

export const invitations = {
  list: () => unwrap<Invitation[]>(api.get('/invitations')),
  create: (emails: string[], isAdmin: boolean, permissions: PermissionCode[]) =>
    unwrap<{ created: CreatedInvitation[]; skipped: Array<{ email: string; reason: string }> }>(
      api.post('/invitations', { emails, isAdmin, permissions }),
    ),
  revoke: (id: string) => api.delete(`/invitations/${id}`).then(() => undefined),
  preview: (token: string) =>
    unwrap<InvitationPreview>(api.get(`/invitations/token/${encodeURIComponent(token)}`)),
  accept: (token: string, payload: { name?: string; password: string }) =>
    unwrap<AuthSession>(
      api.post(`/invitations/token/${encodeURIComponent(token)}/accept`, payload),
    ),
};

export const trash = {
  list: (params: { type?: string; search?: string; skip?: number; take?: number } = {}) =>
    unwrap<Paginated<TrashItem>>(api.get('/trash', { params: { take: 50, ...params } })),
  counts: () =>
    unwrap<{ retentionDays: number; types: string[]; counts: Record<string, number> }>(
      api.get('/trash/counts'),
    ),
  restore: (id: string) => unwrap<TrashItem>(api.post(`/trash/${id}/restore`)),
  purge: (id: string) => api.delete(`/trash/${id}`).then(() => undefined),
};

/** Reabre a sessão (nome e logo do workspace mudam pelas Configurações). */
export const reloadSession = () => unwrap<SessionInfo>(api.get('/auth/me'));

// ----------------------------------------------------------------- DC1b: leads

export const setLeadTags = (id: string, ids: string[]) =>
  unwrap<Lead>(api.put(`/leads/${id}/tags`, { ids }));
export const setLeadLists = (id: string, ids: string[]) =>
  unwrap<Lead>(api.put(`/leads/${id}/lists`, { ids }));
export const setLeadField = (id: string, fieldId: string, value: string | null) =>
  unwrap<Lead>(api.put(`/leads/${id}/additional-fields/${fieldId}`, { value }));
export const uploadLeadImage = (id: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return unwrap<Lead>(api.post(`/leads/${id}/image`, form));
};

/** Alvo das ações em massa: seleção (ids) ou o filtro da tela. */
export type LeadTarget = { ids: string[] } | { filter: string };

export const leadActions = {
  addToLists: (target: LeadTarget, listIds: string[]) =>
    unwrap<{ affected: number }>(
      api.post('/leads/actions/add-to-lists', { ...target, listIds }),
    ),
  removeFromLists: (target: LeadTarget, listIds: string[]) =>
    unwrap<{ affected: number }>(
      api.post('/leads/actions/remove-from-lists', { ...target, listIds }),
    ),
  createList: (target: LeadTarget, name: string, description?: string) =>
    unwrap<{ list: List; affected: number }>(
      api.post('/leads/actions/create-list', { ...target, name, description }),
    ),
  remove: (target: LeadTarget) =>
    unwrap<{ deleted: number } | { job: Job }>(api.post('/leads/actions/delete', target)),
  export: (format: 'CSV' | 'JSON', filter: string) =>
    unwrap<Job>(api.post('/leads/export', { format, filter })),
};

export const leadAddresses = {
  list: (leadId: string) => unwrap<LeadAddressRow[]>(api.get(`/leads/${leadId}/addresses`)),
  create: (leadId: string, payload: Partial<LeadAddressRow>) =>
    unwrap<LeadAddressRow>(api.post(`/leads/${leadId}/addresses`, payload)),
  update: (leadId: string, id: string, payload: Partial<LeadAddressRow>) =>
    unwrap<LeadAddressRow>(api.put(`/leads/${leadId}/addresses/${id}`, payload)),
  remove: (leadId: string, id: string) =>
    api.delete(`/leads/${leadId}/addresses/${id}`).then(() => undefined),
};

export const leadRelations = {
  list: (leadId: string) => unwrap<LeadRelations>(api.get(`/leads/${leadId}/relations`)),
  create: (
    leadId: string,
    payload: { targetId: string; kind: RelationKind; jobTitle?: string; isPrimary?: boolean },
  ) => unwrap<LeadRelations>(api.post(`/leads/${leadId}/relations`, payload)),
  setPrimary: (leadId: string, relationId: string) =>
    unwrap<LeadRelations>(api.put(`/leads/${leadId}/relations/${relationId}/primary`)),
  remove: (leadId: string, relationId: string) =>
    api.delete(`/leads/${leadId}/relations/${relationId}`).then(() => undefined),
  hierarchy: (leadId: string) =>
    unwrap<{ roots: HierarchyNode[] }>(api.get(`/leads/${leadId}/hierarchy`)),
};

export const leadViews = {
  list: () => unwrap<Paginated<SavedView>>(api.get('/lead-views')),
  create: (payload: { name: string; query: string; isShared: boolean }) =>
    unwrap<SavedView>(api.post('/lead-views', payload)),
  update: (id: string, payload: Partial<Pick<SavedView, 'name' | 'query' | 'isShared'>>) =>
    unwrap<SavedView>(api.put(`/lead-views/${id}`, payload)),
  remove: (id: string) => api.delete(`/lead-views/${id}`).then(() => undefined),
};

export const imports = {
  preview: (file: File, delimiter = 'auto') => {
    const form = new FormData();
    form.append('file', file);
    form.append('delimiter', delimiter);
    return unwrap<ImportPreview>(api.post('/imports/preview', form));
  },
  start: (file: File, options: Record<string, unknown>) => {
    const form = new FormData();
    form.append('file', file);
    form.append('options', JSON.stringify(options));
    return unwrap<Job>(api.post('/imports', form));
  },
};

export const jobs = {
  list: () => unwrap<Paginated<Job>>(api.get('/jobs', { params: { take: 50 } })),
  get: (id: string) => unwrap<Job>(api.get(`/jobs/${id}`)),
  /** Arquivo da execução: autenticado, então baixa como blob e dispara o save. */
  download: async (id: string, name: string) => {
    const response = await api.get(`/jobs/${id}/files/${name}`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

export const listAttendants = () =>
  unwrap<Paginated<Attendant>>(api.get('/attendants/crm', { params: { take: 200 } }));

// --------------------------------------------------------------- dashboards

export interface DashboardParams {
  from?: string;
  to?: string;
  pipelineId?: string;
  attendant?: string;
  activityTypeId?: string;
}

export const dashboardApi = {
  businesses: (params: DashboardParams) =>
    unwrap<BusinessDashboard>(api.get('/dashboard/businesses', { params })),
  activities: (params: DashboardParams) =>
    unwrap<ActivityDashboard>(api.get('/dashboard/activities', { params })),
  pipeline: (pipelineId: string, filter: string) =>
    unwrap<PipelineDashboard>(
      api.get(`/pipelines/${pipelineId}/dashboard`, {
        params: { filter: filter || undefined },
      }),
    ),
  /** CSV autenticado: baixa pelo cliente HTTP e entrega como arquivo. */
  export: async (
    tab: 'businesses' | 'activities' | 'pipeline',
    params: DashboardParams & { filter?: string },
  ): Promise<void> => {
    const response = await api.get('/dashboard/export', {
      params: { tab, ...params },
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] ?? '');
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `dashboard-${tab}.csv`;
    const objectUrl = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  },
};

// --------------------------------------------------------- multiatendimento

export const workingHoursApi = {
  ...catalog<WorkingHours>('working-hours'),
};

export const departmentsApi = {
  ...catalog<Department>('departments'),
  members: (id: string) => unwrap<DepartmentMember[]>(api.get(`/departments/${id}/members`)),
  setMembers: (id: string, membershipIds: string[]) =>
    unwrap<DepartmentMember[]>(api.put(`/departments/${id}/members`, { membershipIds })),
};

export interface ConnectionPayload {
  name?: string;
  provider?: ChannelProviderName;
  phoneNumber?: string;
  externalId?: string;
  defaultDepartmentId?: string | null;
  isActive?: boolean;
  config?: { listenGroups?: boolean };
  secrets?: { accessToken?: string; appSecret?: string };
}

/** Instagram (IG1): a conexão nasce pela janela "Conectar Instagram" (login unificado da Meta). */
export const instagramApi = {
  status: () =>
    unwrap<{ configured: boolean; redirectUri: string }>(api.get('/instagram/status')),
  connectUrl: () => unwrap<{ url: string }>(api.get('/instagram/connect')),
};

export const whatsappApi = {
  status: () =>
    unwrap<{ configured: boolean; webhookPath: string }>(api.get('/whatsapp/status')),
  connectUrl: () => unwrap<{ url: string }>(api.get('/whatsapp/connect')),
};

export const connectionsApi = {
  list: () =>
    unwrap<Paginated<ChannelConnection>>(api.get('/instances', { params: { take: 200 } })),
  create: (payload: ConnectionPayload) =>
    unwrap<ChannelConnection>(api.post('/instances', payload)),
  update: (id: string, payload: ConnectionPayload) =>
    unwrap<ChannelConnection>(api.put(`/instances/${id}`, payload)),
  remove: (id: string) => api.delete(`/instances/${id}`).then(() => undefined),
  check: (id: string) => unwrap<ChannelConnection>(api.post(`/instances/${id}/check`)),
  /** Simulador (DC3): um evento no formato da Meta, processado na hora. */
  simulate: (
    id: string,
    payload: { kind: 'text' | 'echo'; contactPhone: string; name?: string; body: string },
  ) =>
    unwrap<{ messages: number; duplicates: number; statuses: number }>(
      api.post(`/channels/${id}/simulate`, payload),
    ),
};

export const memberMultiserviceApi = {
  get: (membershipId: string) =>
    unwrap<MemberMultiservice>(api.get(`/members/${membershipId}/multiservice`)),
  set: (membershipId: string, payload: MemberMultiservice) =>
    unwrap<MemberMultiservice>(api.put(`/members/${membershipId}/multiservice`, payload)),
};

/** URL completa do webhook para colar no painel da Meta. */
export const webhookUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  const base = apiOrigin();
  return `${base}${path}`;
};

// ------------------------------------------------------------- chat ao vivo

export interface ConversationFilters {
  status?: string;
  search?: string;
  departments?: string;
  attendants?: string;
  instances?: string;
  tags?: string;
  stageId?: string;
  openWindow?: 'last24h';
  unread?: boolean;
  sort?: 'recent' | 'oldest' | 'unread';
  skip?: number;
  take?: number;
}

export const chatApi = {
  list: (params: ConversationFilters) =>
    unwrap<Paginated<ChatConversation> & { counts: Record<string, number> }>(
      api.get('/conversations', { params }),
    ),
  get: (id: string) => unwrap<ChatConversation>(api.get(`/conversations/${id}`)),
  messages: (id: string, skip = 0, take = 50) =>
    unwrap<Paginated<ChatMessage>>(
      api.get(`/conversations/${id}/messages`, { params: { skip, take } }),
    ),
  threads: (id: string) => unwrap<ChatThread[]>(api.get(`/conversations/${id}/threads`)),
  byLead: (leadId: string) =>
    unwrap<LeadConversation[]>(api.get(`/conversations/by-lead/${leadId}`)),
  read: (id: string) => unwrap<ChatConversation>(api.post(`/conversations/${id}/read`)),
  finish: (id: string) => unwrap<ChatConversation>(api.post(`/conversations/${id}/finish`)),
  transfer: (
    id: string,
    payload: { departmentId?: string | null; attendantId?: string | null },
  ) => unwrap<ChatConversation>(api.post(`/conversations/${id}/transfer`, payload)),
  attachLead: (id: string, leadId: string) =>
    unwrap<ChatConversation>(api.post(`/conversations/${id}/lead`, { leadId })),
  createLead: (id: string, name?: string) =>
    unwrap<ChatConversation>(api.post(`/conversations/${id}/lead/create`, { name })),
  /** Mídia autenticada: baixa pelo cliente HTTP e devolve uma URL local. */
  mediaUrl: async (path: string): Promise<string> => {
    const relative = path.replace(/^\/api\/v1/, '');
    const response = await api.get(relative, { responseType: 'blob' });
    return URL.createObjectURL(response.data as Blob);
  },
  dashboard: (params: {
    from?: string;
    to?: string;
    departmentId?: string;
    instanceId?: string;
  }) => unwrap<MultiserviceDashboard>(api.get('/dashboard/multiservice', { params })),
};

/* Fase 4 — automações */
export const automationsApi = {
  list: (params: { group?: string; search?: string } = {}) =>
    unwrap<Paginated<Automation>>(
      api.get('/automations', { params: { take: 200, ...params } }),
    ),
  groups: () => unwrap<string[]>(api.get('/automations/groups')),
  get: (id: string) => unwrap<Automation>(api.get(`/automations/${id}`)),
  create: (payload: { name: string; description?: string; group?: string }) =>
    unwrap<Automation>(api.post('/automations', payload)),
  update: (
    id: string,
    payload: {
      name?: string;
      description?: string | null;
      group?: string | null;
      blocks?: AutomationBlock[];
      notes?: AutomationNote[];
    },
  ) => unwrap<Automation>(api.put(`/automations/${id}`, payload)),
  setActive: (id: string, active: boolean) =>
    unwrap<Automation>(api.put(`/automations/${id}/active`, { active })),
  duplicate: (id: string) => unwrap<Automation>(api.post(`/automations/${id}/duplicate`)),
  remove: (id: string) => api.delete(`/automations/${id}`).then(() => undefined),
  versions: (id: string) =>
    unwrap<AutomationVersionSummary[]>(api.get(`/automations/${id}/versions`)),
  rotateWebhook: (id: string) =>
    unwrap<Automation>(api.post(`/automations/${id}/webhook/rotate`)),
  importBundle: (bundle: unknown) =>
    unwrap<AutomationImportReport>(api.post('/automations/import', bundle)),
  exportBundle: (id: string) => unwrap<unknown>(api.get(`/automations/${id}/export`)),
};

export const automationExecutionsApi = {
  list: (
    params: {
      automationId?: string;
      status?: AutomationExecutionStatus;
      problems?: boolean;
      skip?: number;
      take?: number;
    } = {},
  ) =>
    unwrap<Paginated<AutomationExecutionSummary>>(
      api.get('/automation-executions', { params: { take: 50, ...params } }),
    ),
  get: (id: string) => unwrap<AutomationExecution>(api.get(`/automation-executions/${id}`)),
  cancel: (id: string) =>
    unwrap<AutomationExecution>(api.post(`/automation-executions/${id}/cancel`)),
};

/* Fase 5 — chaves de API */
export const apiKeysApi = {
  list: () => unwrap<ApiKey[]>(api.get('/api-keys')),
  create: (payload: {
    name: string;
    isAdmin: boolean;
    permissions: PermissionCode[];
    expiresAt?: string;
  }) => unwrap<ApiKey & { key: string }>(api.post('/api-keys', payload)),
  revoke: (id: string) => api.delete(`/api-keys/${id}`).then(() => undefined),
};

/* Fase 6 — parâmetros do sistema */
export const systemParametersApi = {
  list: () =>
    unwrap<Paginated<SystemParameter>>(
      api.get('/system-parameters', { params: { take: 200 } }),
    ),
  create: (payload: {
    name: string;
    type: ParameterType;
    value: string;
    description?: string;
  }) => unwrap<SystemParameter>(api.post('/system-parameters', payload)),
  update: (
    id: string,
    payload: {
      name?: string;
      type?: ParameterType;
      value?: string;
      description?: string | null;
    },
  ) => unwrap<SystemParameter>(api.put(`/system-parameters/${id}`, payload)),
  remove: (id: string) => api.delete(`/system-parameters/${id}`).then(() => undefined),
};

/* Fase 6 — notificações (sempre as de quem está logado) */
export const notificationsApi = {
  list: (params: { unread?: boolean; skip?: number; take?: number } = {}) =>
    unwrap<{ count: number; unread: number; data: AppNotification[] }>(
      api.get('/notifications', { params }),
    ),
  unreadCount: () => unwrap<{ unread: number }>(api.get('/notifications/unread-count')),
  read: (id: string) => unwrap<AppNotification>(api.post(`/notifications/${id}/read`)),
  readAll: () => unwrap<{ affected: number }>(api.post('/notifications/read-all')),
  clearRead: () => unwrap<{ deleted: number }>(api.delete('/notifications/read')),
  settings: () => unwrap<NotificationSettings>(api.get('/notifications/settings')),
  updateSettings: (patch: Partial<NotificationSettings>) =>
    unwrap<NotificationSettings>(api.put('/notifications/settings', patch)),
};

/** Nova empresa (workspace) — só para quem já é dono de alguma. */
export const createWorkspace = (name: string) =>
  unwrap<{ id: string; name: string; slug: string }>(api.post('/workspaces', { name }));
